import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Input from '../../components/base/Input';
import PhoneInput from '../../components/base/PhoneInput';
import Button from '../../components/base/Button';
import { resolveApiBaseUrl } from '../../utils/apiClient';
import { BrandRule } from '../../components/brand/BrandRule';

// White sections split by hairlines, teal headings (THEME.md §3).
const H2 = 'text-[1.375rem] font-bold text-[var(--pv-color-primary)]';
const LABEL = 'mb-1.5 block text-sm font-semibold text-[var(--pv-color-text)]';
const CONTACT_ROW = 'border-b border-[var(--pv-border-subtle)] py-4 first:pt-0';
const CONTACT_LINK = 'text-[1rem] font-medium text-[var(--pv-color-text)] underline-offset-4 transition-colors hover:text-[var(--pv-color-primary)] hover:underline';

type SubjectOption = {
  value: string;
  label: string;
};

type ContactFormFields = {
  nameLabel?: string;
  namePlaceholder?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  phoneLabel?: string;
  phonePlaceholder?: string;
  subjectLabel?: string;
  subjectOptions?: SubjectOption[];
  messageLabel?: string;
  messagePlaceholder?: string;
  messageCounter?: string;
};

type ContactFormCopy = {
  title?: string;
  subtitle?: string;
  replyTime?: string;
  success?: string;
  error?: string;
  submitLabel?: string;
  submitting?: string;
  fields?: ContactFormFields;
};

type NewsletterCopy = {
  title?: string;
  subtitle?: string;
  interestLabel?: string;
  interestHelper?: string;
  interests?: string[];
  fields?: {
    emailLabel?: string;
    emailPlaceholder?: string;
    firstNameLabel?: string;
    firstNamePlaceholder?: string;
  };
  submitLabel?: string;
  submitting?: string;
  success?: string;
  error?: string;
};

type AlertsCopy = {
  contactSuccess: string;
  contactError: string;
  newsletterSuccess: string;
  newsletterError: string;
};

export default function ContactPage() {
  const { t, i18n } = useTranslation(['contact', 'common']);
  const isRTL = i18n.dir() === 'rtl';
  const textAlignClass = isRTL ? 'text-right' : 'text-left';

  const hero = useMemo(() => {
    const raw = t('contact.hero', { returnObjects: true });
    if (raw && typeof raw === 'object') {
      return raw as { title?: string; subtitle?: string };
    }
    return { title: '', subtitle: '' };
  }, [t, i18n.language]);

  const newsletter = useMemo<NewsletterCopy>(() => {
    const raw = t('contact.newsletter', { returnObjects: true });
    if (raw && typeof raw === 'object') {
      return raw as NewsletterCopy;
    }
    return {};
  }, [t, i18n.language]);

  const formCopy = useMemo<ContactFormCopy>(() => {
    const raw = t('contact.form', { returnObjects: true });
    if (raw && typeof raw === 'object') {
      return raw as ContactFormCopy;
    }
    return {};
  }, [t, i18n.language]);

  const formFields: ContactFormFields = formCopy.fields ?? {};
  const submitLabel = formCopy.submitLabel ?? 'Send message';
  const submittingLabel = formCopy.submitting ?? 'Sending…';

  const newsletterFields = newsletter.fields ?? ({} as NonNullable<NewsletterCopy['fields']>);
  const newsletterSubmitLabel = newsletter.submitLabel ?? 'Subscribe';
  const newsletterSubmittingLabel = newsletter.submitting ?? 'Subscribing…';

  const alerts = useMemo<AlertsCopy>(() => {
    const raw = t('contact.alerts', { returnObjects: true });
    if (raw && typeof raw === 'object') {
      const data = raw as Partial<AlertsCopy>;
      return {
        contactSuccess: data.contactSuccess ?? '',
        contactError: data.contactError ?? '',
        newsletterSuccess: data.newsletterSuccess ?? '',
        newsletterError: data.newsletterError ?? '',
      };
    }
    return {
      contactSuccess: '',
      contactError: '',
      newsletterSuccess: '',
      newsletterError: '',
    };
  }, [t, i18n.language]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [newsletterData, setNewsletterData] = useState({
    email: '',
    firstName: '',
    interests: [] as string[],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNewsletterSubmitting, setIsNewsletterSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const interestOptions = Array.isArray(newsletter.interests) ? newsletter.interests : [];
  const subjectOptions: SubjectOption[] = Array.isArray(formFields.subjectOptions)
    ? formFields.subjectOptions
    : [];

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNewsletterChange = (field: string, value: string) => {
    setNewsletterData((prev) => ({ ...prev, [field]: value }));
  };

  const handleInterestChange = (interest: string) => {
    setNewsletterData((prev) => {
      const exists = prev.interests.includes(interest);
      return {
        ...prev,
        interests: exists ? prev.interests.filter((item) => item !== interest) : [...prev.interests, interest],
      };
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const API_BASE_URL = resolveApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        console.warn('Contact API returned error:', response.status);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Contact API network error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsNewsletterSubmitting(true);
    setNewsletterStatus('idle');

    try {
      const newsletterEndpoint = import.meta.env.VITE_NEWSLETTER_API_URL ||
        `${resolveApiBaseUrl()}/newsletter/subscribe`;

      const response = await fetch(newsletterEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newsletterData.email,
          firstName: newsletterData.firstName,
          interests: newsletterData.interests,
        }),
      });

      if (response.ok) {
        setNewsletterStatus('success');
        setNewsletterData({ email: '', firstName: '', interests: [] });
      } else {
        setNewsletterStatus('error');
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      setNewsletterStatus('error');
    } finally {
      setIsNewsletterSubmitting(false);
    }
  };

  const messageCounterLabel = (count: number) =>
    formFields.messageCounter
      ? formFields.messageCounter.replace('{{count}}', count.toString())
      : `${count}/500`;

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)] text-[var(--pv-color-text-muted)]" dir={i18n.dir()} lang={i18n.language}>
      <Navigation />

      {/* One header, then the form beside the ways to reach us, each listed once
          (owner, 2026-10-07: the phone, e-mail and hours used to appear in two
          channel cards AND a «معلومات سريعة» card). */}
      <header className="mx-auto max-w-5xl px-6 pb-10 pt-12 text-center sm:pt-16">
        <h1 className="text-[2rem] font-bold leading-tight text-[var(--pv-color-primary)] sm:text-[2.5rem]">{hero.title}</h1>
        <BrandRule className="mx-auto mt-4" />
        <p className="mt-3 text-[1.0625rem]">{hero.subtitle}</p>
      </header>

      <main>
        <section className="border-t border-[var(--pv-border-subtle)] py-12">
          <div className="mx-auto grid max-w-5xl gap-12 px-6 lg:grid-cols-5">
            {/* The form */}
            <div className="lg:col-span-3">
              <h2 className={`${H2} ${textAlignClass}`}>{formCopy.title}</h2>
              <p className={`mt-2 text-[1rem] ${textAlignClass}`}>{formCopy.subtitle}</p>
              {formCopy.replyTime && (
                <p className={`mt-1.5 flex items-center gap-1.5 text-[0.875rem] ${textAlignClass}`}>
                  <i className="ri-time-line text-base leading-none text-[var(--pv-color-primary)]" aria-hidden="true"></i>
                  {formCopy.replyTime}
                </p>
              )}

              {submitStatus === 'success' && (
                <div className="mt-6 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4" role="alert" aria-live="polite">
                  <i className="ri-checkbox-circle-fill text-xl text-emerald-600" aria-hidden="true"></i>
                  <p className="flex-1 text-sm font-medium text-emerald-800">{alerts.contactSuccess}</p>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mt-6 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4" role="alert">
                  <i className="ri-error-warning-fill text-xl text-rose-600" aria-hidden="true"></i>
                  <p className="flex-1 text-sm font-medium text-rose-800">{alerts.contactError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                <div>
                  <label htmlFor="contact-name" className={`${LABEL} ${textAlignClass}`}>
                    {formFields.nameLabel}
                  </label>
                  <Input
                    id="contact-name"
                    name="name"
                    autoComplete="name"
                    placeholder={formFields.namePlaceholder}
                    value={formData.name}
                    onChange={(event) => handleInputChange('name', event.target.value)}
                    required
                    dir={isRTL ? 'rtl' : 'ltr'}
                    className="w-full"
                  />
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label htmlFor="contact-email" className={`${LABEL} ${textAlignClass}`}>
                      {formFields.emailLabel}
                    </label>
                    <Input
                      type="email"
                      id="contact-email"
                      name="email"
                      autoComplete="email"
                      spellCheck={false}
                      placeholder={formFields.emailPlaceholder}
                      value={formData.email}
                      onChange={(event) => handleInputChange('email', event.target.value)}
                      required
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className={`${LABEL} ${textAlignClass}`}>
                      {formFields.phoneLabel}
                    </label>
                    <PhoneInput
                      id="contact-phone"
                      value={formData.phone}
                      onChange={(value) => handleInputChange('phone', value)}
                      placeholder={formFields.phonePlaceholder}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-subject" className={`${LABEL} ${textAlignClass}`}>
                    {formFields.subjectLabel}
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    value={formData.subject}
                    onChange={(event) => handleInputChange('subject', event.target.value)}
                    className={`w-full rounded-xl border border-[var(--pv-border-strong)] bg-[var(--pv-color-surface)] px-4 py-3 text-sm text-[var(--pv-color-text)] transition-all focus:border-[var(--pv-color-primary)] focus:outline-none focus:ring-4 focus:ring-[var(--pv-color-surface-sunken)] ${textAlignClass}`}
                    required
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    {subjectOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className={`${LABEL} ${textAlignClass}`}>
                    {formFields.messageLabel}
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    placeholder={formFields.messagePlaceholder}
                    value={formData.message}
                    onChange={(event) => handleInputChange('message', event.target.value)}
                    rows={5}
                    className={`w-full resize-y rounded-xl border border-[var(--pv-border-strong)] bg-[var(--pv-color-surface)] px-4 py-3 text-sm text-[var(--pv-color-text)] transition-all focus:border-[var(--pv-color-primary)] focus:outline-none focus:ring-4 focus:ring-[var(--pv-color-surface-sunken)] ${textAlignClass}`}
                    maxLength={500}
                    required
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                  <p className={`mt-1.5 text-xs ${textAlignClass}`}>
                    {messageCounterLabel(formData.message.length)}
                  </p>
                </div>

                <Button type="submit" disabled={isSubmitting} className="w-full" size="lg">
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-lg" aria-hidden="true"></i>
                      <span>{submittingLabel}</span>
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-fill text-lg" aria-hidden="true"></i>
                      <span>{submitLabel}</span>
                    </span>
                  )}
                </Button>
              </form>
            </div>

            {/* The ways to reach us, once */}
            <aside className="lg:col-span-2">
              <h2 className={`${H2} ${textAlignClass}`}>{isRTL ? 'طرق التواصل' : 'Ways to reach us'}</h2>
              <dl className="m-0 mt-5">
                <div className={CONTACT_ROW}>
                  <dt className="flex items-center gap-2 text-[0.875rem]">
                    <i className="ri-phone-line text-[1.125rem] leading-none text-[var(--pv-color-primary)]" aria-hidden="true"></i>
                    {isRTL ? 'الهاتف وواتساب' : 'Phone & WhatsApp'}
                  </dt>
                  <dd className="m-0 mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <a href="tel:+966505608271" className={CONTACT_LINK} dir="ltr">+966 50 560 8271</a>
                    <a
                      href="https://wa.me/966505608271"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[0.875rem] font-medium text-[var(--pv-color-primary)] underline-offset-4 hover:underline"
                    >
                      <i className="ri-whatsapp-line text-base leading-none text-[#1DA851]" aria-hidden="true"></i>
                      {isRTL ? 'راسلنا على واتساب' : 'Message us on WhatsApp'}
                    </a>
                  </dd>
                </div>
                <div className={CONTACT_ROW}>
                  <dt className="flex items-center gap-2 text-[0.875rem]">
                    <i className="ri-mail-line text-[1.125rem] leading-none text-[var(--pv-color-primary)]" aria-hidden="true"></i>
                    {isRTL ? 'البريد الإلكتروني' : 'Email'}
                  </dt>
                  <dd className="m-0 mt-1.5">
                    <a href="mailto:info@safaqat24.sa" className={CONTACT_LINK}>info@safaqat24.sa</a>
                  </dd>
                </div>
                <div className={CONTACT_ROW}>
                  <dt className="flex items-center gap-2 text-[0.875rem]">
                    <i className="ri-time-line text-[1.125rem] leading-none text-[var(--pv-color-primary)]" aria-hidden="true"></i>
                    {isRTL ? 'ساعات العمل' : 'Working hours'}
                  </dt>
                  <dd className="m-0 mt-1.5 text-[1rem] text-[var(--pv-color-text)]">
                    {isRTL ? 'يومياً: 9:00 ص - 6:00 م' : 'Daily: 9:00 AM - 6:00 PM'}
                  </dd>
                </div>
              </dl>
            </aside>
          </div>
        </section>

        {/* Newsletter */}
        <section className="border-t border-[var(--pv-border-subtle)] py-12">
          <div className="mx-auto max-w-3xl px-6">
            <h2 className={`${H2} text-center`}>{newsletter.title}</h2>
            <p className="mt-2 text-center text-[1rem]">{newsletter.subtitle}</p>

            {newsletterStatus === 'success' && (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4" role="alert" aria-live="polite">
                <i className="ri-checkbox-circle-fill text-xl text-emerald-600" aria-hidden="true"></i>
                <p className="flex-1 text-sm font-medium text-emerald-800">{alerts.newsletterSuccess}</p>
              </div>
            )}
            {newsletterStatus === 'error' && (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4" role="alert">
                <i className="ri-error-warning-fill text-xl text-rose-600" aria-hidden="true"></i>
                <p className="flex-1 text-sm font-medium text-rose-800">{alerts.newsletterError}</p>
              </div>
            )}

            <form onSubmit={handleNewsletterSubmit} className="mt-6 space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="newsletter-email" className={`${LABEL} ${textAlignClass}`}>
                    {newsletterFields.emailLabel}
                  </label>
                  <Input
                    type="email"
                    id="newsletter-email"
                    name="email"
                    autoComplete="email"
                    spellCheck={false}
                    placeholder={newsletterFields.emailPlaceholder}
                    value={newsletterData.email}
                    onChange={(event) => handleNewsletterChange('email', event.target.value)}
                    required
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>
                <div>
                  <label htmlFor="newsletter-firstName" className={`${LABEL} ${textAlignClass}`}>
                    {newsletterFields.firstNameLabel}
                  </label>
                  <Input
                    id="newsletter-firstName"
                    name="firstName"
                    autoComplete="given-name"
                    placeholder={newsletterFields.firstNamePlaceholder}
                    value={newsletterData.firstName}
                    onChange={(event) => handleNewsletterChange('firstName', event.target.value)}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>
              </div>

              {interestOptions.length > 0 && (
                <fieldset>
                  <legend className={`mb-2 flex w-full items-center justify-between gap-3 ${textAlignClass}`}>
                    <span className="text-sm font-semibold text-[var(--pv-color-text)]">{newsletter.interestLabel}</span>
                    <span className="text-xs">{newsletter.interestHelper}</span>
                  </legend>
                  {/* Interests as pills: the chosen ones take the decision-D3 selected look. */}
                  <div className="flex flex-wrap gap-2">
                    {interestOptions.map((interest) => {
                      const checked = newsletterData.interests.includes(interest);
                      return (
                        <label
                          key={interest}
                          className={`inline-flex h-10 cursor-pointer items-center gap-2 rounded-full border px-4 text-[0.875rem] font-medium transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--pv-color-primary)]/30 ${
                            checked
                              ? 'border-[var(--pv-color-primary)] bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]'
                              : 'border-[var(--pv-border-strong)] text-[var(--pv-color-text)] hover:border-[var(--pv-color-primary)]'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleInterestChange(interest)}
                            className="sr-only"
                          />
                          {checked && <i className="ri-check-line text-base leading-none" aria-hidden="true"></i>}
                          <span>{interest}</span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              )}

              <Button type="submit" disabled={isNewsletterSubmitting} className="w-full" size="lg">
                {isNewsletterSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-lg" aria-hidden="true"></i>
                    <span>{newsletterSubmittingLabel}</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-mail-send-line text-lg" aria-hidden="true"></i>
                    <span>{newsletterSubmitLabel}</span>
                  </span>
                )}
              </Button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
